import { useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useQueryClient } from '@tanstack/react-query';
import { Highlighter } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { AppShell } from '@/components/AppShell';
import { HighlightCardSkeleton, HighlightList } from '@/components/HighlightList';
import { NoteContent } from '@/components/NoteContent';
import { ProfileSnippet } from '@/components/ProfileSnippet';
import { SelectionHighlighter } from '@/components/SelectionHighlighter';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useEventById } from '@/hooks/useAddressableEvent';
import { useAuthor } from '@/hooks/useAuthor';
import { useHighlightsOfSource } from '@/hooks/useHighlights';
import { getDisplayName } from '@/lib/getDisplayName';
import { HIGHLIGHT_KIND } from '@/lib/highlights';
import { relativeTime } from '@/lib/relativeTime';
import { HighlightDetailPage } from './HighlightDetailPage';

interface NotePageProps {
  id: string;
}

export function NotePage({ id }: NotePageProps) {
  const note = useEventById(id);
  const { highlights, isPending: highlightsPending } = useHighlightsOfSource({ e: id });
  const queryClient = useQueryClient();
  const noteRef = useRef<HTMLDivElement>(null);
  const author = useAuthor(note.data?.pubkey);

  const handlePublished = (published: NostrEvent) => {
    queryClient.setQueryData<NostrEvent[]>(['nostr', 'highlights-of', '', id, ''], (old) => [
      published,
      ...(old ?? []),
    ]);
  };

  useSeoMeta({
    title: 'Note — glowmark',
    description: 'A note and its highlights on glowmark.',
  });

  // Highlights (kind 9802) get their own permalink view with comment thread.
  if (note.data && note.data.kind === HIGHLIGHT_KIND) {
    return <HighlightDetailPage event={note.data} />;
  }

  return (
    <AppShell>
      <div className="container max-w-2xl py-8 space-y-8">
        {note.isPending ? (
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-full bg-muted animate-pulse" />
                <div className="h-4 w-32 rounded bg-muted animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full rounded bg-muted animate-pulse" />
                <div className="h-4 w-4/5 rounded bg-muted animate-pulse" />
              </div>
            </CardContent>
          </Card>
        ) : !note.data ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">
                This note couldn't be found on your relays.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6 space-y-4" ref={noteRef}>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ProfileSnippet pubkey={note.data.pubkey} />
                <time dateTime={new Date(note.data.created_at * 1000).toISOString()}>
                  {relativeTime(note.data.created_at)}
                </time>
              </div>
              <NoteContent event={note.data} className="text-base" />
            </CardContent>
          </Card>
        )}

        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Highlighter className="size-4.5 text-primary" />
            Highlights
            {highlights.length > 0 && (
              <Badge variant="secondary" className="ml-1">{highlights.length}</Badge>
            )}
          </h2>
          {highlightsPending ? (
            <div className="space-y-4">
              <HighlightCardSkeleton />
            </div>
          ) : highlights.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 px-6 text-center">
                <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                  No one has highlighted this note yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <HighlightList highlights={highlights} />
          )}
        </section>
      </div>

      {note.data && (
        <SelectionHighlighter
          containerRef={noteRef}
          sourceContent={note.data.content}
          source={{
            type: 'note',
            id: note.data.id,
            authorPubkey: note.data.pubkey,
            label: `Note by ${getDisplayName(author.data?.metadata, note.data.pubkey)}`,
          }}
          onPublished={handlePublished}
        />
      )}
    </AppShell>
  );
}
